import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useClipboard } from 'use-clipboard-copy';
import { usePost } from '@tribeplatform/react-sdk/hooks';
import { Divider } from '@tribeplatform/react-ui-kit/Divider';
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons';
import { Input } from '@tribeplatform/react-ui-kit/Input';
import { Link } from '@tribeplatform/react-ui-kit/Link';
import { Select } from '@tribeplatform/react-ui-kit/Select';
import { Slider } from '@tribeplatform/react-ui-kit/Slider';
import { Toggle } from '@tribeplatform/react-ui-kit/Toggle';
import { useDomain } from '../common';
import { ErrorBoundary } from '../common/components/ErrorBoundary';
import { T, useStaticIntl } from '../i18n';
const toQueryString = obj => Object.keys(obj)
    .map(key => `${key}=${obj[key]}`)
    .join('&');
const getIframe = (iframeSrc, { height, title }) => {
    return `<iframe frameborder="no" src="${iframeSrc}" height="${height}px" width="100%" title="${title}" />`;
};
const viewOptionsFactory = ({ $t }) => ({
    all: $t({
        defaultMessage: 'Content and Replies',
        id: 'EmbedViewOptions.ContentAndReplies',
    }),
    'content-only': $t({
        defaultMessage: 'Content Only',
        id: 'EmbedViewOptions.Content',
    }),
    'replies-only': $t({
        defaultMessage: 'Replies Only',
        id: 'EmbedViewOptions.Replies',
    }),
});
export const PostEmbedCreator = ({ postId }) => {
    const viewOptions = useStaticIntl(viewOptionsFactory);
    const [copied, setCopied] = useState(false);
    const clipboard = useClipboard();
    const { data: post, isFetching: fetchingPost } = usePost({
        variables: { id: postId },
    });
    const { domain, subfolder } = useDomain();
    const loading = fetchingPost;
    const baseUrl = `https://${domain}${subfolder}/embed/post/${postId}`;
    const [iframeHeight, setIframeHeight] = useState(360);
    const [iframeSrcParams, setIframeSrcParams] = useState({
        view: 'all',
        readonly: true,
        show_full_content: true,
    });
    const iframeSrc = `${baseUrl}?${toQueryString(iframeSrcParams)}`;
    const set = (key, value) => {
        setIframeSrcParams({
            ...iframeSrcParams,
            [key]: value,
        });
    };
    if (loading) {
        return (_jsx("div", { className: "flex justify-center items-center w-full h-96", children: _jsx(SpinnerIcon, { className: "animate-spin absolute h-9 w-9" }, void 0) }, void 0));
    }
    const iframeToCopy = getIframe(iframeSrc, {
        height: iframeHeight,
        title: post === null || post === void 0 ? void 0 : post.title,
    });
    return (_jsxs(_Fragment, { children: [_jsx(Input, { onClick: e => e.currentTarget.select(), value: iframeToCopy, readOnly: true, trailingAddon: _jsx(Link, { onClick: () => {
                        clipboard.copy(iframeToCopy);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                    }, variant: "neutral", children: _jsx(T, { id: "Generics.CopyTooltip", defaultMessage: "{copied, select, true {Copied} other {Copy}}", values: { copied } }, void 0) }, void 0) }, void 0), _jsx(Divider, {}, void 0), _jsxs("div", { className: "mb-4 space-y-5", children: [_jsxs("div", { className: "flex justify-between items-center", children: [_jsx("h5", { children: _jsx(T, { defaultMessage: "View", id: "ShareModal.PostEmbedCreator.View" }, void 0) }, void 0), _jsxs(Select, { value: iframeSrcParams.view, onChange: newValue => {
                                    set('view', newValue);
                                }, children: [_jsx(Select.Button, { children: viewOptions[iframeSrcParams.view] }, void 0), _jsx(Select.Items, { children: Object.entries(viewOptions).map(([key, label]) => (_jsx(Select.Item, { value: key, children: label }, key))) }, void 0)] }, void 0)] }, void 0), _jsxs("div", { className: "flex justify-between items-center", children: [_jsx("h5", { children: _jsx(T, { defaultMessage: "Read only", id: "ShareModal.PostEmbedCreator.ReadOnly" }, void 0) }, void 0), _jsx(Toggle, { checked: iframeSrcParams.readonly, onChange: () => {
                                    set('readonly', !iframeSrcParams.readonly);
                                } }, void 0)] }, void 0)] }, void 0), _jsx("h5", { children: _jsx(T, { defaultMessage: "Embed height: {iframe_height}px", id: "Share.EmbedHeight", values: {
                        iframe_height: iframeHeight,
                    } }, void 0) }, void 0), _jsx("div", { className: "mb-2", children: _jsx(Slider, { value: iframeHeight, step: 10, min: 200, max: 1000, onChange: setIframeHeight }, void 0) }, void 0), _jsxs(ErrorBoundary, { children: [_jsx("h5", { className: "mb-1", children: _jsx(T, { defaultMessage: "Embed preview", id: "Share.EmbedPreview" }, void 0) }, void 0), _jsx("div", { className: "p-3 bg-skeleton overflow-auto h-96 rounded-md", children: _jsx("iframe", { src: iframeSrc, height: `${iframeHeight}px`, width: "100%", title: post === null || post === void 0 ? void 0 : post.title }, void 0) }, void 0)] }, void 0)] }, void 0));
};
//# sourceMappingURL=PostEmbedCreator.js.map